// @mui material components
import {
  Snackbar,
  Alert,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Button,
  Grid,
  Rating,
  Divider,
  ListItem,
  ListItemText,
  Stack,
} from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDButton from "theme/components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";
import PieChart from "examples/Charts/PieChart";

import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios.js";
import DataTable from "theme/Tables/DataTable";
import usePageTitle from "app/pageTitle";
import GoogleMapReact from "google-map-react";
import AppConfig from "appConfig";

function PSAInsightsPage() {
  const pageTitle = "Product Sampling - Insights";
  usePageTitle(pageTitle);

  const navigate = useNavigate();
  const { ref } = useParams();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };
  const [refreshPage, setRefreshPage] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);

  const [insights, setInsights] = useState(null);
  const [campaign, setCampaign] = useState(null);

  const [mapZoom, setMapZoom] = useState(13);

  function getPieChartColors(data) {
    const colors = ["info", "primary", "secondary", "error", "warning", "success"];
    const bgColors = [];
    data.every(() => {
      bgColors.push(colors[Math.floor(Math.random() * colors.length)]);
      return true;
    });

    return bgColors;
  }

  function getSentimentColors() {
    return ["success", "secondary", "error"];
  }

  function getPieChartLabels(data) {
    const labels = [];
    data.every((row) => {
      labels.push(row.label);
      return true;
    });

    return labels;
  }

  function getPieChartData(data) {
    const datas = [];
    data.every((row) => {
      datas.push(row.count);
      return true;
    });

    return datas;
  }

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get(`portal/psa/${ref}/insights.json`)
        .then((result) => {
          setShowLoading(false);
          if (result.data.status === "success") {
            setCampaign(result.data.data.campaign);
            setInsights(result.data.data.insights);

            setPageLoaded(true);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine ? "" : "Make sure you have an active internet connection"
            }`
          );
          setOpen(true);
        });
    };

    fetchData();
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <MDBox mb={5}>
        <MDBox mt={1} mb={2} display="flex" justifyContent="space-between">
          <MDBox>
            <Button
              color="dark"
              size="small"
              startIcon={<Icon>west</Icon>}
              onClick={() => {
                navigate(`/trade-marketing/promotions/product-sampling/${ref}`);
              }}
            >
              Back
            </Button>
          </MDBox>
        </MDBox>
        {pageLoaded && (
          <MDBox>
            <MDBox>
              <Grid container spacing={3}>
                <Grid item xs={12} md={3} lg={3}>
                  <MDBox>
                    <Card>
                      <MDBox p={2} align="center">
                        <MDTypography>
                          <Icon>person_alt</Icon>
                        </MDTypography>
                        <MDTypography fontSize={24}>{insights.sampled}</MDTypography>
                        <MDTypography variant="body2">
                          Customers
                          <br />
                          Sampled
                        </MDTypography>
                      </MDBox>
                    </Card>
                  </MDBox>
                </Grid>
                <Grid item xs={12} md={3} lg={3}>
                  <MDBox>
                    <Card>
                      <MDBox p={2} align="center">
                        <MDTypography>
                          <Icon>rate_review</Icon>
                        </MDTypography>
                        <MDTypography fontSize={24}>{insights.feedbacks}</MDTypography>
                        <MDTypography variant="body2">
                          Feedback
                          <br />
                          Received
                        </MDTypography>
                      </MDBox>
                    </Card>
                  </MDBox>
                </Grid>
                <Grid item xs={12} md={3} lg={3}>
                  <MDBox>
                    <Card>
                      <MDBox p={2} align="center">
                        <MDTypography>
                          <Icon>inventory</Icon>
                        </MDTypography>
                        <MDTypography fontSize={24}>{insights.totalQty}</MDTypography>
                        <MDTypography variant="body2">
                          Quantity
                          <br />
                          Distributed
                        </MDTypography>
                      </MDBox>
                    </Card>
                  </MDBox>
                </Grid>
                <Grid item xs={12} md={3} lg={3}>
                  <MDBox>
                    <Card>
                      <MDBox p={2} align="center">
                        <MDTypography>{campaign.currency}</MDTypography>
                        <MDTypography fontSize={24}>
                          {(insights.totalVol / 100).toLocaleString("en", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </MDTypography>
                        <MDTypography variant="body2">
                          Volume
                          <br />
                          Distributed
                        </MDTypography>
                      </MDBox>
                    </Card>
                  </MDBox>
                </Grid>
              </Grid>
            </MDBox>
            <Stack mt={5} display="flex" gap={3}>
              {insights.products.map((product) => (
                <MDBox>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4} lg={4}>
                      <MDBox>
                        <Card>
                          <MDBox p={2}>
                            <MDBox mb={1}>
                              <ListItem divider>
                                <ListItemText
                                  primary={product.name}
                                  secondary={`Sampled: ${product.costs.sampled}`}
                                />
                              </ListItem>
                            </MDBox>
                          </MDBox>
                        </Card>
                        <MDBox mt={3}>
                          <Card>
                            <MDBox p={2}>
                              <MDTypography variant="body2" fontSize={13} mb={1}>
                                Rating (AVG)
                              </MDTypography>
                              <Rating value={product.rating} readOnly />
                            </MDBox>
                          </Card>
                        </MDBox>
                      </MDBox>
                      <MDBox mt={3}>
                        <Grid container spacing={1}>
                          <Grid item xs={12} md={6} lg={6}>
                            <Card>
                              <MDBox p={1} align="center">
                                <MDTypography>
                                  <Icon>inventory</Icon>
                                </MDTypography>
                                <MDTypography fontSize={18}>
                                  {product.costs.quantity.toLocaleString("en", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </MDTypography>
                                <MDTypography variant="body2" fontSize={13}>
                                  Quantity
                                </MDTypography>
                              </MDBox>
                            </Card>
                          </Grid>
                          <Grid item xs={12} md={6} lg={6}>
                            <Card>
                              <MDBox p={1} align="center">
                                <MDTypography>{campaign.currency}</MDTypography>
                                <MDTypography fontSize={18}>
                                  {(product.costs.volume / 100).toLocaleString("en", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </MDTypography>
                                <MDTypography variant="body2" fontSize={13}>
                                  Spend
                                </MDTypography>
                              </MDBox>
                            </Card>
                          </Grid>
                        </Grid>
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} md={8} lg={8}>
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6} lg={6}>
                          <MDBox sx={{ height: "100%" }}>
                            <PieChart
                              sx={{ marginTop: "0px" }}
                              // icon={{ color: "info", component: "leaderboard" }}
                              title="Buy Sentiment (%)"
                              description=""
                              chart={{
                                labels: Object.keys(product.buy.percentages),
                                datasets: {
                                  backgroundColors: getSentimentColors(),
                                  data: Object.values(product.buy.percentages),
                                },
                              }}
                            />
                          </MDBox>
                        </Grid>
                        <Grid item xs={12} md={6} lg={6}>
                          <MDBox sx={{ height: "100%" }}>
                            <PieChart
                              sx={{ marginTop: "0px" }}
                              // icon={{ color: "info", component: "leaderboard" }}
                              title="Recommend Sentiment (%)"
                              description=""
                              chart={{
                                labels: Object.keys(product.recommend.percentages),
                                datasets: {
                                  backgroundColors: getSentimentColors(),
                                  data: Object.values(product.recommend.percentages),
                                },
                              }}
                            />
                          </MDBox>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </MDBox>
              ))}
            </Stack>
            {insights.spreadData.length > 0 && (
              <MDBox mt={5}>
                <Card>
                  <MDBox p={3}>
                    <MDBox mb={3} display="flex" justifyContent="space-between">
                      <MDBox>
                        <MDTypography variant="h6" fontWeight="bold">
                          Location HeatMap
                        </MDTypography>
                      </MDBox>
                      <MDBox>
                        <MDButton
                          size="small"
                          variant="outlined"
                          color="dark"
                          iconOnly
                          onClick={() => {
                            let pZoom = mapZoom;
                            pZoom -= 1;
                            pZoom = pZoom < 4 ? 4 : pZoom;
                            setMapZoom(pZoom);
                          }}
                        >
                          <Icon>remove</Icon>
                        </MDButton>
                        &nbsp;
                        <MDTypography variant="caption" fontSize={10} fontWeight="light">
                          Zoom
                        </MDTypography>
                        &nbsp;
                        <MDButton
                          size="small"
                          variant="outlined"
                          color="dark"
                          iconOnly
                          onClick={() => {
                            let pZoom = mapZoom;
                            pZoom += 1;
                            pZoom = pZoom > 20 ? 20 : pZoom;
                            setMapZoom(pZoom);
                          }}
                        >
                          <Icon>add</Icon>
                        </MDButton>
                      </MDBox>
                    </MDBox>
                    <MDBox sx={{ height: "65vh", width: "100%" }}>
                      <GoogleMapReact
                        bootstrapURLKeys={{ key: AppConfig.google_map_key }}
                        defaultCenter={{
                          lat: insights.spreadData[0].latitude,
                          lng: insights.spreadData[0].longitude,
                        }}
                        defaultZoom={mapZoom}
                        zoom={mapZoom}
                      >
                        {insights.spreadData.map((loc) => (
                          <MDBox lat={loc.latitude} lng={loc.longitude}>
                            <MDTypography fontSize={24}>
                              <Icon color="error">location_on</Icon>
                            </MDTypography>
                          </MDBox>
                        ))}
                      </GoogleMapReact>
                    </MDBox>
                  </MDBox>
                </Card>
              </MDBox>
            )}
          </MDBox>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default PSAInsightsPage;
